import { useGetBrandById } from "@entities/brand/lib/hooks/useGetBrandById";
import BillingModal from "@features/organisation/ui/BillingModal";
import { ToastService } from "@shared/lib/services";
import { IBrandBilling } from "@shared/lib/types/brandBilling";
import { Button } from "@shared/ui/Button";
import { Select } from "@shared/ui/Select";
import {
  Avatar,
  Card,
  Col,
  Descriptions,
  Empty,
  Flex,
  Form,
  Row,
  Space,
  Statistic,
  Typography,
} from "antd";
import {
  CreditCard,
  Info,
  Landmark,
  Pen,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { BILLING_TYPE } from "../lib/constants";
import useEditFinanceSpocs from "../lib/hooks/useEditFinanceSpocs";
import { useSearchBrandUser } from "../lib/hooks/useSearchBrandUser";
import styles from "./styles.module.scss";

export default function BillingInfo({
  billingDetails,
  billingMode,
  brandId,
  brandOrganisationId,
  refetch,
}: {
  billingDetails?: IBrandBilling;
  billingMode?: BILLING_TYPE;
  brandId?: string;
  brandOrganisationId: string;
  refetch?: () => void;
}) {
  const { brand } = useGetBrandById(brandId || "");
  const isBillingEmpty = !billingDetails;
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingModalMode, setBillingModalMode] = useState<
    "create" | "edit" | "createBrandBilling" | "editBrandBilling"
  >("create");
  const [showFinanceForm, setShowFinanceForm] = useState(false);

  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedEmployeeSearch] = useDebounce(employeeSearch, 500);
  const [isAddingContact, setIsAddingContact] = useState(false);

  const { employeesData, isFetchingEmployees } = useSearchBrandUser({
    brandOrganisationId: brandOrganisationId || "",
    searchQuery: debouncedEmployeeSearch,
    accountStatuses: ["ACTIVE"],
    limit: 1000,
  });

  const {
    editFinanceSpocs,
    isEditFinanceSpocsLoading,
    isEditFinanceSpocsSuccess,
    editFinanceSpocsError,
    editFinanceSpocsVariables,
  } = useEditFinanceSpocs(() => {
    setIsAddingContact(false);
    setEmployeeSearch("");
    ToastService.success("Finance person added successfully");
    refetch?.();
  });

  const toAddEmployeesList = useMemo(() => {
    return employeesData?.data?.data?.filter((employee) => {
      return !billingDetails?.financePocs?.some(
        (financePoc) => financePoc.userId === employee._id
      );
    });
  }, [employeesData, billingDetails]);

  useEffect(() => {
    if (isEditFinanceSpocsSuccess) {
      setShowFinanceForm(false);
      ToastService.success("Finance user updated successfully.");
    } else if (editFinanceSpocsError) {
      ToastService.error(editFinanceSpocsError.message);
    }
  }, [isEditFinanceSpocsSuccess, editFinanceSpocsError]);

  console.log("finance pocs: ", billingDetails);

  return (
    <div className={styles.billingTab}>
      <Card
        title={
          <Flex
            align="center"
            gap={12}
            justify="space-between"
            className={styles.fullWidth}
          >
            <Flex align="center" gap={12}>
              <Avatar
                shape="square"
                size={32}
                icon={<CreditCard size={18} />}
                className={styles.cardIconAvatar}
              />
              <div>
                <Typography.Title level={5} className={styles.cardTitleLg}>
                  Billing Details
                </Typography.Title>
                {!isBillingEmpty && billingMode === BILLING_TYPE.Single && (
                  <p className={styles.billingModeText}>
                    Mode:{" "}
                    <span className={styles.billingMode}>
                      {billingMode === "Single"
                        ? "Single Billing"
                        : "Multiple Billing"}
                    </span>
                  </p>
                )}
              </div>
            </Flex>
            <Button
              type="link"
              icon={<Pen size={16} />}
              onClick={() => {
                setBillingModalMode(() => {
                  // will be called in the organisation details section only
                  if (isBillingEmpty && !billingMode) {
                    return "create";
                  }
                  //   create new single type billingDetails
                  else if (
                    isBillingEmpty &&
                    billingMode === BILLING_TYPE.Single
                  ) {
                    return "create";
                  }
                  //   create new multiple type billingDetails for a brand
                  else if (
                    isBillingEmpty &&
                    billingMode === BILLING_TYPE.Multiple
                  ) {
                    return "createBrandBilling";
                  }
                  //   edit existing billingDetails for organisation
                  else if (
                    !isBillingEmpty &&
                    billingMode === BILLING_TYPE.Single
                  ) {
                    return "edit";
                  }
                  //   edit existing billingDetails for a specific brand
                  else if (
                    !isBillingEmpty &&
                    billingMode === BILLING_TYPE.Multiple
                  ) {
                    return "editBrandBilling";
                  }
                  // keep the default as create
                  return "create";
                });
                setBillingModalOpen(true);
              }}
            >
              Edit
            </Button>
          </Flex>
        }
        className={`${styles.card}`}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Card
                key={brand?._id}
                className={styles.brandBillingCard}
                styles={{
                  header: {
                    borderTop: "4px solid #635bff",
                    borderBottom: "none",
                  },
                }}
                title={
                  <Space>
                    <Avatar
                      className={styles.landmarkAvatar}
                      shape="square"
                      size="large"
                      icon={<Landmark />}
                    />
                    <div>
                      <Typography.Title
                        level={5}
                        style={{ marginBottom: 0, marginTop: 10 }}
                      >
                        {brand?.name} Billing Information
                      </Typography.Title>
                      {billingDetails ? (
                        <Typography.Text
                          style={{
                            fontSize: 12,
                            color: "#697386",
                            fontWeight: 400,
                          }}
                        >
                          <Info size={12} />{" "}
                          {billingMode === BILLING_TYPE.Single
                            ? "Unified for all brands."
                            : "Brand-specific billing details"}
                        </Typography.Text>
                      ) : null}
                    </div>
                  </Space>
                }
              >
                {billingDetails ? (
                  <>
                    <Descriptions
                      items={[
                        {
                          key: "1",
                          label: "Party Name",
                          children: billingDetails?.name,
                        },
                        {
                          key: "2",
                          label: "Billing Email",
                          children: billingDetails?.emailId || "-",
                        },
                        {
                          key: "3",
                          label: "GSTIN",
                          children: billingDetails?.gst,
                        },
                        {
                          key: "4",
                          label: "PAN",
                          children: billingDetails?.pan || "-",
                        },
                        {
                          key: "5",
                          label: "Address",
                          children: billingDetails?.address?.addressLine1,
                        },
                      ]}
                      layout="horizontal"
                      column={1}
                      styles={{
                        content: {
                          //   borderBottom: "1px solid #e3e8ee",
                        },
                        label: {
                          //   borderBottom: "1px solid #e3e8ee",
                          width: 150,
                        },
                      }}
                    />
                  </>
                ) : (
                  <Empty description="No billing details found." />
                )}
              </Card>
            </Space>
          </Col>

          <Col xs={24} lg={12}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Card
                title={
                  <Space style={{ marginTop: 5 }}>
                    <Avatar
                      className={styles.creditAvatar}
                      shape="square"
                      size="large"
                      icon={<CreditCard />}
                    />
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      Commercial Terms
                    </Typography.Title>
                  </Space>
                }
                className={styles.commercialCard}
                styles={{
                  header: {
                    borderBottom: "none",
                  },
                }}
              >
                {brand ? (
                  <Typography.Title
                    level={5}
                    style={{ marginBottom: 10, marginTop: 0 }}
                  >
                    {brand?.name}
                  </Typography.Title>
                ) : null}
                {billingDetails ? (
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Card size="small" className={styles.statisticCard}>
                        <Statistic
                          title="Service Cost"
                          value={`${billingDetails?.serviceChargeRate || "-"}%`}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card size="small" className={styles.statisticCard}>
                        <Statistic
                          title="Payment Terms"
                          value={`${billingDetails?.paymentTermsInDays || "-"} Days`}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card size="small" className={styles.statisticCard}>
                        <Statistic
                          title="Credit Limit"
                          value={`₹${billingDetails?.creditLimit || "-"}`}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card size="small" className={styles.statisticCard}>
                        <Statistic
                          title="SAC Code"
                          value={billingDetails?.sacCode || "-"}
                        />
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <Empty description="No commercial terms found." />
                )}
              </Card>

              {/* <Card
                title={
                  <Space>
                    <Avatar shape="square" size="large" icon={<Briefcase />} />
                    <Title level={5}>Service Agreement</Title>
                  </Space>
                }
                extra={<Button icon={<Download size={14} />}>Download</Button>}
                className={styles.agreementCard}
              ></Card> */}
            </Space>
          </Col>
          <Col span={24}>
            <Card
              title={
                <Space>
                  <Avatar shape="square" size="large" icon={<Users />} />
                  <div>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      Finance Team
                    </Typography.Title>
                    <Typography.Text>
                      Invoices & payment links will be sent here
                    </Typography.Text>
                  </div>
                </Space>
              }
              className={styles.financeCard}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                {billingDetails?.financePocs &&
                billingDetails?.financePocs?.length > 0 ? (
                  billingDetails?.financePocs?.map((contact) => (
                    <Card key={contact.userId} size="small">
                      <Flex justify="space-between" align="center">
                        <Space>
                          <Avatar>{contact.user?.firstName?.charAt(0)}</Avatar>
                          <div>
                            <Typography.Text
                              className={styles.financeContactName}
                            >
                              {contact.user?.firstName} {contact.user?.lastName}
                            </Typography.Text>
                            <br />
                            <Typography.Text
                              className={styles.financeContactEmail}
                            >
                              {contact.user?.emailId}
                            </Typography.Text>
                          </div>
                        </Space>
                        <Button
                          type="text"
                          danger
                          icon={<Trash2 size={14} />}
                          loading={
                            isEditFinanceSpocsLoading &&
                            !editFinanceSpocsVariables?.financePocUserIds?.includes(
                              contact.userId
                            )
                          }
                          onClick={() => {
                            editFinanceSpocs({
                              brandOrganisationId: brandOrganisationId || "",
                              financePocUserIds:
                                billingDetails?.financePocs
                                  ?.filter(
                                    (financePoc) =>
                                      financePoc.userId !== contact.userId
                                  )
                                  ?.map((financePoc) => financePoc.userId) ||
                                [],
                              brandId: brandId,
                            });
                          }}
                        />
                      </Flex>
                    </Card>
                  ))
                ) : (
                  <Empty description="No finance team found." />
                )}

                {billingDetails && (
                  <>
                    {isAddingContact ? (
                      <Form
                        onFinish={(values) => {
                          if (values.userId) {
                            editFinanceSpocs({
                              brandOrganisationId: brandOrganisationId || "",
                              financePocUserIds: [
                                ...(billingDetails?.financePocs?.map(
                                  (financePoc) => financePoc.userId
                                ) || []),
                                values.userId,
                              ],
                              brandId: brandId,
                            });
                          }
                        }}
                        layout="vertical"
                      >
                        <Form.Item
                          label="User"
                          required
                          name="userId"
                          rules={[
                            {
                              required: true,
                              message: "Please select a user",
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            allowClear
                            filterOption={false}
                            searchValue={employeeSearch}
                            onSearch={(value) => setEmployeeSearch(value)}
                            loading={isFetchingEmployees}
                            options={toAddEmployeesList?.map((employee) => ({
                              label: `${employee.firstName} ${employee.lastName} (${employee.emailId})`,
                              value: employee._id,
                            }))}
                            placeholder="Select User"
                          />
                        </Form.Item>

                        <Space style={{ marginTop: 10 }}>
                          <Button
                            onClick={() => {
                              setIsAddingContact(false);
                              setEmployeeSearch("");
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={isEditFinanceSpocsLoading}
                          >
                            Save
                          </Button>
                        </Space>
                      </Form>
                    ) : (
                      <Button
                        type="dashed"
                        block
                        icon={<Plus size={14} />}
                        onClick={() => {
                          setIsAddingContact(true);
                        }}
                      >
                        Add Finance Person
                      </Button>
                    )}
                  </>
                )}
              </Space>
            </Card>
          </Col>
        </Row>
        <BillingModal
          open={billingModalOpen}
          onClose={() => {
            setBillingModalOpen(false);
          }}
          initialData={
            billingModalMode === "edit" ||
            billingModalMode === "editBrandBilling"
              ? billingDetails
              : undefined
          }
          mode={billingModalMode}
          organisationId={brandOrganisationId}
          brandId={brandId}
        />
      </Card>
    </div>
  );
}
